import { supabase } from '../../config/supabase';
import {
  mergeRowerProfileWithRootProfile,
  type RootProfileIdentity,
} from '../../utils/mergeRowerProfileWithRootProfile';

export type SocialLinks = {
  instagram?: string | null;
  tiktok?: string | null;
  youtube?: string | null;
};

/** Saved on rower_profiles.profile_links */
export type ProfileLink = {
  url: string;
  label?: string | null;
};

/** Saved on rower_profiles.profile_awards; files live in storage bucket rower-awards */
export type ProfileAward = {
  storage_path: string;
  label?: string | null;
  kind: 'image' | 'document';
  mime_type?: string | null;
};

export function parseProfileAward(raw: unknown): ProfileAward | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const storage_path = o.storage_path;
  if (typeof storage_path !== 'string' || !storage_path) return null;
  const kind = o.kind === 'document' ? 'document' : 'image';
  return {
    storage_path,
    label: typeof o.label === 'string' ? o.label : null,
    kind,
    mime_type: typeof o.mime_type === 'string' ? o.mime_type : null,
  };
}

const AWARD_ALLOWED_MIMES = new Set([
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
  'application/pdf',
]);

const MIME_TO_EXT: Record<string, string> = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
  'image/gif': 'gif',
  'application/pdf': 'pdf',
};

function awardFileKindFromMime(mime: string): 'image' | 'document' {
  return mime.startsWith('image/') ? 'image' : 'document';
}

/** Per-field booleans plus concept2_training team map (`teamId` → shared). */
export type RowerProfileVisibility = Record<string, boolean | Record<string, boolean>>;

export interface RowerProfile {
  id: string;
  first_name: string;
  last_name: string;
  preferred_name?: string | null;
  profile_id?: string | null;
  // Athlete primary info
  date_of_birth?: string | null;
  gender?: 'male' | 'female' | 'open' | null;
  height_cm?: number | null;
  weight_kg?: number | null;
  club?: string | null;
  bio?: string | null;
  erg_2k_seconds?: number | null;
  gpa?: number | null;
  sat_score?: number | null;
  act_score?: number | null;
  avatar_url?: string | null;
  email?: string | null;
  profile_visibility?: RowerProfileVisibility;
  phone?: string | null;
  based_city?: string | null;
  based_region?: string | null;
  based_country_code?: string | null;
  social_links?: SocialLinks | null;
  profile_links?: ProfileLink[] | null;
  profile_awards?: ProfileAward[] | null;
  measurement_system?: 'metric' | 'imperial';
}

export interface RowerTeam {
  id: string;
  name: string;
  division?: string | null;
  sport?: string | null;
  gender?: string | null;
  team_tier?: string | null;
  current_season?: string | null;
  organization_id?: string | null;
  organization_program_id?: string | null;
}

export interface RowerSession {
  id: string;
  session_id: string;
  session_name?: string | null;
  session_date: string;
  start_time: string;
  location?: string | null;
  boat_type?: string | null;
  coach_name?: string | null;
  crew_name?: string | null;
  team_id?: string | null;
  team_name?: string | null;
}

export interface RowingClub {
  id: number;
  name: string;
  city: string | null;
  state: string | null;
}

export const rowerService = {
  async searchRowingClubs(query: string, limit = 25): Promise<RowingClub[]> {
    const trimmed = query.trim();
    let q = supabase
      .from('rowing_clubs')
      .select('id, name, city, state')
      .order('name', { ascending: true })
      .limit(limit);

    if (trimmed) {
      const safe = trimmed.replace(/,/g, ' ').replace(/\s+/g, ' ').trim();
      q = q.or(`name.ilike.%${safe}%,city.ilike.%${safe}%,state.ilike.%${safe}%`);
    }

    const { data, error } = await q;
    if (error) {
      console.error('[rowerService] searchRowingClubs error:', error);
      return [];
    }

    return (data || []) as RowingClub[];
  },

  async getRowerProfileByUserId(userId: string): Promise<RowerProfile | null> {
    if (!userId) return null;

    const { data, error } = await supabase
      .from('rower_profiles')
      .select('*')
      .eq('profile_id', userId)
      .maybeSingle();

    if (error) {
      if (error.code !== 'PGRST116') {
        console.error('[rowerService] getRowerProfileByUserId error:', error);
      }
      return null;
    }

    if (!data) return null;

    const rower = data as unknown as RowerProfile;
    const { data: rootProfile, error: rootError } = await supabase
      .from('profiles')
      .select('first_name, last_name, date_of_birth')
      .eq('id', userId)
      .maybeSingle();

    if (rootError && rootError.code !== 'PGRST116') {
      console.error('[rowerService] getRowerProfileByUserId profiles error:', rootError);
    }

    return mergeRowerProfileWithRootProfile(rower, rootProfile as RootProfileIdentity | null);
  },

  async getRowerTeams(rowerProfileId: string): Promise<RowerTeam[]> {
    if (!rowerProfileId) return [];

    const { data, error } = await supabase
      .from('team_roster_entries')
      .select(
        `
        teams:teams (
          id,
          name,
          division,
          sport,
          gender,
          team_tier,
          current_season,
          organization_id,
          organization_program_id
        )
      `
      )
      .eq('rower_id', rowerProfileId);

    if (error) {
      console.error('[rowerService] getRowerTeams error:', error);
      return [];
    }

    const teams = (data || []).map((row: any) => row.teams).filter(Boolean) as RowerTeam[];

    return teams;
  },

  async getRowerSessions(rowerProfileId: string): Promise<RowerSession[]> {
    if (!rowerProfileId) return [];

    const { data, error } = await supabase
      .from('telemetry_rowers')
      .select(
        `
        session_id,
        telemetry_sessions:telemetry_sessions (
          id,
          session_id,
          session_name,
          session_date,
          start_time,
          location,
          boat_type,
          coach_name,
          coach_id,
          coach_profile:profiles!fk_telemetry_sessions_coach_id (
            full_name
          ),
          crew_name,
          team_id,
          team:team_id (
            name
          )
        )
      `
      )
      .eq('roster_id', rowerProfileId);

    if (error) {
      console.error('[rowerService] getRowerSessions error:', error);
      return [];
    }

    const sessions = (data || [])
      .map((row: any) => {
        const s = row.telemetry_sessions;
        if (!s) return null;
        const derivedCoachName = (s.coach_profile?.full_name as string | null | undefined) || null;
        const derivedTeamName = (s.team?.name as string | null | undefined) || null;
        return {
          ...(s as RowerSession),
          coach_name: derivedCoachName || (s.coach_name as string | null | undefined) || null,
          team_id: (s.team_id as string | null | undefined) || null,
          team_name: derivedTeamName,
        } satisfies RowerSession;
      })
      .filter(Boolean) as RowerSession[];

    return sessions;
  },

  async uploadAvatar(rowerProfileId: string, file: File): Promise<string> {
    const ext = file.name.split('.').pop()?.toLowerCase() || 'jpg';
    const path = `${rowerProfileId}/avatar.${ext}`;

    const { error } = await supabase.storage
      .from('rower-avatars')
      .upload(path, file, { upsert: true, contentType: file.type });

    if (error) {
      console.error('[rowerService] uploadAvatar error:', error);
      throw new Error('Failed to upload avatar');
    }

    return path;
  },

  awardFileKind: awardFileKindFromMime,

  async uploadProfileAwardFile(rowerProfileId: string, file: File): Promise<ProfileAward> {
    const mime = file.type || 'application/octet-stream';
    if (!AWARD_ALLOWED_MIMES.has(mime)) {
      throw new Error('Invalid file type. Use JPG, PNG, WebP, GIF, or PDF.');
    }
    const ext = MIME_TO_EXT[mime] || 'bin';
    const fileId = crypto.randomUUID();
    const path = `${rowerProfileId}/awards/${fileId}.${ext}`;

    const { error } = await supabase.storage
      .from('rower-awards')
      .upload(path, file, { upsert: false, contentType: mime });

    if (error) {
      console.error('[rowerService] uploadProfileAwardFile error:', error);
      throw new Error('Failed to upload file');
    }

    return {
      storage_path: path,
      label: null,
      kind: awardFileKindFromMime(mime),
      mime_type: mime,
    };
  },

  async removeProfileAwardFromStorage(storagePath: string): Promise<void> {
    const { error } = await supabase.storage.from('rower-awards').remove([storagePath]);
    if (error) {
      console.error('[rowerService] removeProfileAwardFromStorage error:', error);
      throw new Error('Failed to remove file');
    }
  },

  async getProfileAwardSignedUrl(storagePath: string, expiresSec = 3600): Promise<string | null> {
    const { data, error } = await supabase.storage
      .from('rower-awards')
      .createSignedUrl(storagePath, expiresSec);
    if (error) {
      console.error('[rowerService] getProfileAwardSignedUrl error:', error);
      return null;
    }
    return data?.signedUrl ?? null;
  },

  async updateRowerProfile(
    rowerProfileId: string,
    updates: {
      first_name?: string;
      last_name?: string;
      preferred_name?: string | null;
      date_of_birth?: string | null;
      gender?: 'male' | 'female' | 'open' | null;
      height_cm?: number | null;
      weight_kg?: number | null;
      club?: string | null;
      bio?: string | null;
      erg_2k_seconds?: number | null;
      gpa?: number | null;
      sat_score?: number | null;
      act_score?: number | null;
      avatar_url?: string | null;
      profile_visibility?: RowerProfileVisibility;
      phone?: string | null;
      based_city?: string | null;
      based_region?: string | null;
      based_country_code?: string | null;
      social_links?: SocialLinks | null;
      measurement_system?: 'metric' | 'imperial';
      profile_links?: ProfileLink[] | null;
      profile_awards?: ProfileAward[] | null;
    }
  ): Promise<RowerProfile> {
    const { data: updated, error } = await supabase
      .from('rower_profiles')
      .update(updates)
      .eq('id', rowerProfileId)
      .select('*')
      .maybeSingle();

    if (error) {
      console.error('[rowerService] updateRowerProfile error:', error);
      throw new Error('Unable to update profile');
    }

    if (!updated) {
      throw new Error('Profile not found');
    }

    return updated as unknown as RowerProfile;
  },

  /**
   * Get a single session by ID (for rower detail page)
   */
  async getRowerSession(sessionId: string, rowerProfileId: string): Promise<RowerSession | null> {
    if (!sessionId || !rowerProfileId) return null;

    const { data, error } = await supabase
      .from('telemetry_rowers')
      .select(
        `
        session_id,
        telemetry_sessions:telemetry_sessions (
          id,
          session_id,
          session_name,
          session_date,
          start_time,
          location,
          boat_type,
          coach_name,
          coach_id,
          coach_profile:profiles!fk_telemetry_sessions_coach_id (
            full_name
          ),
          crew_name,
          team_id,
          team:team_id (
            name
          )
        )
      `
      )
      .eq('session_id', sessionId)
      .eq('roster_id', rowerProfileId)
      .maybeSingle();

    if (error) {
      console.error('[rowerService] getRowerSession error:', error);
      return null;
    }

    if (!data || !data.telemetry_sessions) return null;

    const s: any = data.telemetry_sessions;
    const derivedCoachName = (s?.coach_profile?.full_name as string | null | undefined) || null;
    const derivedTeamName = (s?.team?.name as string | null | undefined) || null;
    return {
      ...(s as RowerSession),
      coach_name: derivedCoachName || (s?.coach_name as string | null | undefined) || null,
      team_id: (s?.team_id as string | null | undefined) || null,
      team_name: derivedTeamName,
    };
  },

  /**
   * Get rower's position and session seat_id in a specific session
   */
  async getRowerPositionInSession(
    sessionId: string,
    rowerProfileId: string
  ): Promise<{ position: number | null; seatId: string | null } | null> {
    if (!sessionId || !rowerProfileId) return null;

    const { data, error } = await supabase
      .from('telemetry_rowers')
      .select('position, seat_id')
      .eq('session_id', sessionId)
      .eq('roster_id', rowerProfileId)
      .maybeSingle();

    if (error) {
      console.error('[rowerService] getRowerPositionInSession error:', error);
      return null;
    }

    if (!data) return null;

    return {
      position: data.position ? Number(data.position) : null,
      seatId: (data.seat_id as string) ?? null,
    };
  },
};
